"use client";

import { cn } from "@/lib/utils";
import { MinusIcon, PlusIcon } from "lucide-react";
import { ComponentProps } from "react";

import { parseProductQuantity } from "../utils/parseProductQuantity";

type ProductQuantityProps = {
  value: number;
  onDecrease: () => void;
  onIncrease: () => void;
  onChange: (value: number) => void;
  max?: number;
  disableIncrease?: boolean;
  className?: string;
} & Omit<ComponentProps<"div">, "onChange">;

export default function ProductQuantity({
  value,
  onDecrease,
  onIncrease,
  onChange,
  max,
  disableIncrease,
  className,
  ...props
}: ProductQuantityProps) {
  return (
    <div
      className={cn("flex h-10 items-center border border-black", className)}
      {...props}
    >
      <button
        type="button"
        aria-label="Decrease quantity"
        disabled={value <= 1}
        className="flex size-10 items-center justify-center border-r border-black disabled:cursor-not-allowed disabled:opacity-50"
        onClick={onDecrease}
      >
        <MinusIcon strokeWidth={1} className="size-4" />
      </button>
      <input
        type="number"
        name="quantity"
        min={1}
        max={max}
        value={value}
        aria-label="Quantity"
        inputMode="numeric"
        className="size-10 [appearance:textfield] border-0 bg-transparent px-2 text-center text-base outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        onChange={(event) => {
          const next = parseProductQuantity(event.currentTarget.value, max);
          if (next === undefined) return;
          onChange(next);
        }}
      />
      <button
        type="button"
        aria-label="Increase quantity"
        disabled={disableIncrease}
        className={cn(
          "flex size-10 items-center justify-center border-l border-black",
          disableIncrease && "cursor-not-allowed opacity-50",
        )}
        onClick={onIncrease}
      >
        <PlusIcon strokeWidth={1} className="size-4" />
      </button>
    </div>
  );
}
